import { apiService } from './api.service';
import { AdminDashboard, CompanyDashboard, DemoClient, NearExpiryAccount, ExpiredAccount } from 'types/dashboard';
import type {
  ScanAnalytics,
  ScanAnalyticsPeriod,
  ScanAnalyticsScope,
  RecentScansFilters,
  RecentScansPage,
} from 'types/scanAnalytics';
import { normalizeScanAnalytics, normalizeRecentScansPage } from 'utils/scanAnalyticsNormalize';
import { PaginatedResponse } from 'types/index';
import { normalizePaginatedResponse } from 'utils/pagination';

class DashboardService {
  async getAccountDashboard(): Promise<CompanyDashboard> {
    const response = await apiService.get<CompanyDashboard>(`/dashboard/Account`);
    const data = (response as any)?.data || response;
    if (!data) throw new Error((response as any)?.message || 'Failed to load account dashboard');
    return data as CompanyDashboard;
  }

  async getAdminDashboard(): Promise<AdminDashboard> {
    const response = await apiService.get<AdminDashboard>(`/dashboard/Admin`);
    const data = (response as any)?.data || response;
    if (!data) throw new Error((response as any)?.message || 'Failed to load admin dashboard');
    return data as AdminDashboard;
  }

  async getDemoClients(
    page: number = 1,
    pageSize: number = 10,
    options?: { search?: string; accountType?: string }
  ): Promise<PaginatedResponse<DemoClient>> {
    const params = new URLSearchParams({
      page: String(page),
      pageSize: String(pageSize),
    });
    if (options?.search?.trim()) {
      params.set('search', options.search.trim());
    }
    if (options?.accountType && options.accountType !== 'all') {
      params.set('accountType', options.accountType);
    }
    const response = await apiService.get(
      `/dashboard/DemoClients?${params.toString()}`
    );
    return normalizePaginatedResponse<DemoClient>(response, {
      page,
      limit: pageSize,
      fallbackMessage: 'Failed to load demo clients',
    });
  }

  async getNearExpiryAccounts(
    page: number = 1,
    pageSize: number = 10,
    options?: { search?: string; accountType?: string }
  ): Promise<PaginatedResponse<NearExpiryAccount>> {
    const params = new URLSearchParams({
      page: String(page),
      pageSize: String(pageSize),
    });
    if (options?.search?.trim()) {
      params.set('search', options.search.trim());
    }
    if (options?.accountType && options.accountType !== 'all') {
      params.set('accountType', options.accountType);
    }
    const response = await apiService.get(
      `/dashboard/NearExpiry?${params.toString()}`
    );
    return normalizePaginatedResponse<NearExpiryAccount>(response, {
      page,
      limit: pageSize,
      fallbackMessage: 'Failed to load near-expiry accounts',
    });
  }

  async getExpiredAccounts(
    page: number = 1,
    pageSize: number = 10,
    options?: { search?: string; accountType?: string; isDemo?: boolean }
  ): Promise<PaginatedResponse<ExpiredAccount>> {
    const params = new URLSearchParams({
      page: String(page),
      pageSize: String(pageSize),
    });
    if (options?.search?.trim()) {
      params.set('search', options.search.trim());
    }
    if (options?.accountType && options.accountType !== 'all') {
      params.set('accountType', options.accountType);
    }
    if (typeof options?.isDemo === 'boolean') {
      params.set('isDemo', String(options.isDemo));
    }
    const response = await apiService.get(
      `/dashboard/ExpiredAccounts?${params.toString()}`
    );
    return normalizePaginatedResponse<ExpiredAccount>(response, {
      page,
      limit: pageSize,
      fallbackMessage: 'Failed to load expired accounts',
    });
  }

  async getScanAnalytics(
    period: ScanAnalyticsPeriod = 'daily',
    scope?: ScanAnalyticsScope
  ): Promise<ScanAnalytics> {
    const params = { period };
    const response = scope?.employeeScopeId
      ? await apiService.get<ScanAnalytics>(
          `/employee/${scope.employeeScopeId}/scan-analytics`,
          { params }
        )
      : scope?.companyScopeId
        ? await apiService.get<ScanAnalytics>(
            `/company/${scope.companyScopeId}/scan-analytics`,
            { params }
          )
        : scope?.individualScopeId
          ? await apiService.get<ScanAnalytics>(
              `/individual/${scope.individualScopeId}/scan-analytics`,
              { params }
            )
          : await apiService.get<ScanAnalytics>(`/dashboard/ScanAnalytics`, { params });
    const data = (response as any)?.data || response;
    return normalizeScanAnalytics(data);
  }

  async getRecentScans(options: {
    page?: number;
    pageSize?: number;
    from?: string | null;
    to?: string | null;
    employeeId?: string | null;
    individualId?: string | null;
    scope?: ScanAnalyticsScope;
    exportAll?: boolean;
  }): Promise<RecentScansPage> {
    const page = options.page ?? 1;
    const pageSize = options.pageSize ?? 10;
    const params: Record<string, string | number | boolean> = {
      page,
      pageSize,
      exportAll: options.exportAll ?? false,
    };
    if (options.from) params.from = options.from;
    if (options.to) params.to = options.to;
    if (options.employeeId) params.employeeId = options.employeeId;
    if (options.individualId) params.individualId = options.individualId;

    const scope = options.scope;
    const response = scope?.employeeScopeId
      ? await apiService.get(`/employee/${scope.employeeScopeId}/recent-scans`, {
          params,
          metadata: { skipGlobalLoading: true },
        } as any)
      : scope?.companyScopeId
        ? await apiService.get(`/company/${scope.companyScopeId}/recent-scans`, {
            params,
            metadata: { skipGlobalLoading: true },
          } as any)
        : scope?.individualScopeId
          ? await apiService.get(`/individual/${scope.individualScopeId}/recent-scans`, {
              params,
              metadata: { skipGlobalLoading: true },
            } as any)
          : await apiService.get(`/dashboard/RecentScans`, {
              params,
              metadata: { skipGlobalLoading: true },
            } as any);

    const data = (response as any)?.data || response;
    return normalizeRecentScansPage(data, page, pageSize);
  }
}

export const dashboardService = new DashboardService();
export default dashboardService;
